import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { 
  LayoutDashboard, Package, ShoppingCart, Users, UserCheck, 
  Settings, ChevronRight, Plus, Edit, Trash2, Check, X, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AdminDashboardPage = () => {
  const location = useLocation();
  
  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Overview", exact: true },
    { path: "/admin/products", icon: Package, label: "Products" },
    { path: "/admin/orders", icon: ShoppingCart, label: "Orders" },
    { path: "/admin/users", icon: Users, label: "Users" },
    { path: "/admin/affiliates", icon: UserCheck, label: "Affiliates" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.05)] p-4 sticky top-24">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Admin Panel</h2>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'bg-[#D4AF37] text-black'
                        : 'text-[#A3A3A3] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                    }`}
                    data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Routes>
              <Route index element={<AdminOverview />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="affiliates" element={<AdminAffiliates />} />
            </Routes>
          </main>
        </div>
      </div>
    </Layout>
  );
};

// Admin Overview
const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchSchedulerStatus();
    // Refresh scheduler status every 30 seconds
    const interval = setInterval(fetchSchedulerStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/scheduler/status`);
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch scheduler status:", error);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 bg-[#121212] animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="admin-overview">
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h1>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="stat-value">{stats?.total_users || 0}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.total_orders || 0}</div>
          <div className="stat-label">Total Orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-[#00E676]">₹{stats?.total_revenue?.toLocaleString() || 0}</div>
          <div className="stat-label">Revenue</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.total_affiliates || 0}</div>
          <div className="stat-label">Affiliates</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/admin/products" className="flex items-center justify-between p-3 bg-[#0a0a0a] hover:bg-[rgba(255,255,255,0.05)] transition-colors">
              <span className="text-[#A3A3A3]">Manage Products</span>
              <ChevronRight className="w-4 h-4 text-[#737373]" />
            </Link>
            <Link to="/admin/orders" className="flex items-center justify-between p-3 bg-[#0a0a0a] hover:bg-[rgba(255,255,255,0.05)] transition-colors">
              <span className="text-[#A3A3A3]">View Orders</span>
              <ChevronRight className="w-4 h-4 text-[#737373]" />
            </Link>
            <Link to="/admin/affiliates" className="flex items-center justify-between p-3 bg-[#0a0a0a] hover:bg-[rgba(255,255,255,0.05)] transition-colors">
              <span className="text-[#A3A3A3]">Affiliate Applications</span>
              <ChevronRight className="w-4 h-4 text-[#737373]" />
            </Link>
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-white mb-4">System Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[#A3A3A3]">Price Scheduler (5 min)</span>
              <span className={`text-sm ${schedulerStatus?.scheduler_running ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                {schedulerStatus?.scheduler_running ? '● Running' : '○ Stopped'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#A3A3A3]">Last Price Update</span>
              <span className="text-[#D4AF37] text-sm">
                {formatTime(schedulerStatus?.last_price_update)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#A3A3A3]">Next Update</span>
              <span className="text-[#A3A3A3] text-sm">
                {formatTime(schedulerStatus?.next_run_time)}
              </span>
            </div>
            <div className="pt-3 border-t border-[rgba(255,255,255,0.05)]">
              <div className="flex items-center justify-between">
                <span className="text-[#A3A3A3]">Gold Rate</span>
                <span className="text-[#D4AF37] font-mono">
                  ₹{schedulerStatus?.current_prices?.gold_per_gram?.toLocaleString() || 0}/g
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[#A3A3A3]">Silver Rate</span>
                <span className="text-[#C0C0C0] font-mono">
                  ₹{schedulerStatus?.current_prices?.silver_per_gram?.toLocaleString() || 0}/g
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Products
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    metal_type: "gold",
    purity: "24K",
    weight: 1,
    weight_unit: "g",
    description: "",
    image_url: "",
    is_active: true
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formData);
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/admin/products`, formData);
        toast.success("Product created!");
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      metal_type: product.metal_type,
      purity: product.purity,
      weight: product.weight,
      weight_unit: product.weight_unit,
      description: product.description,
      image_url: product.image_url,
      is_active: product.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success("Product deleted!");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      metal_type: "gold",
      purity: "24K",
      weight: 1,
      weight_unit: "g",
      description: "",
      image_url: "",
      is_active: true
    });
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Products</h1>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }} 
          className="btn-gold flex items-center gap-2"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" /> Add Product
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-[#121212] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Type</th>
                <th>Weight</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img src={product.image_url} alt="" className="w-10 h-10 object-cover bg-[#0a0a0a]" />
                      <span className="text-white">{product.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge-${product.metal_type}`}>{product.purity} {product.metal_type}</span>
                  </td>
                  <td className="text-[#A3A3A3]">{product.weight}{product.weight_unit}</td>
                  <td>
                    <span className={`status-badge ${product.is_active ? 'delivered' : 'cancelled'}`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(product)} className="p-2 hover:bg-[rgba(255,255,255,0.05)]">
                        <Edit className="w-4 h-4 text-[#A3A3A3]" />
                      </button>
                      <button onClick={() => handleDelete(product.id)} className="p-2 hover:bg-[rgba(255,255,255,0.05)]">
                        <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.1)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 mt-4">
            <div>
              <Label>Product Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Metal Type</Label>
                <Select value={formData.metal_type} onValueChange={(v) => setFormData({ ...formData, metal_type: v })}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Purity</Label>
                <Select value={formData.purity} onValueChange={(v) => setFormData({ ...formData, purity: v })}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
                    <SelectItem value="22K">22K</SelectItem>
                    <SelectItem value="24K">24K</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Weight Unit</Label>
                <Select value={formData.weight_unit} onValueChange={(v) => setFormData({ ...formData, weight_unit: v })}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
                    <SelectItem value="mg">Milligrams (mg)</SelectItem>
                    <SelectItem value="g">Grams (g)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Weight</Label>
              <Input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
                className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]"
              />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]"
              />
            </div>
            <Button onClick={handleSubmit} className="btn-gold">
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Orders
const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const url = statusFilter === "all" 
        ? `${API}/admin/orders`
        : `${API}/admin/orders?status=${statusFilter}`;
      const response = await axios.get(url);
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success("Order status updated!");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statuses = ["all", "pending_payment", "paid", "processing", "shipped", "delivered", "cancelled"];

  return (
    <div data-testid="admin-orders">
      <h1 className="text-2xl font-bold text-white mb-6">Orders</h1>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {statuses.map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 text-sm whitespace-nowrap ${
              statusFilter === status
                ? 'bg-[#D4AF37] text-black'
                : 'bg-[#121212] text-[#A3A3A3] border border-[rgba(255,255,255,0.05)]'
            }`}
          >
            {status === "all" ? "All" : status.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-[#121212] animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <ShoppingCart className="empty-state-icon" />
          <p className="text-[#737373]">No orders found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="dashboard-card">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <p className="text-white font-mono">#{order.id.slice(0, 8)}</p>
                    <span className={`status-badge ${order.status}`}>{order.status.replace("_", " ")}</span>
                  </div>
                  <p className="text-sm text-[#A3A3A3]">{order.user_name} • {order.user_email}</p>
                  <p className="text-sm text-[#737373]">
                    {order.shipping_city}, {order.shipping_state} - {order.shipping_pincode}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-xl text-[#D4AF37]">₹{order.total?.toLocaleString()}</p>
                  <Select value={order.status} onValueChange={(status) => updateOrderStatus(order.id, status)}>
                    <SelectTrigger className="w-[140px] bg-[#0a0a0a] border-[rgba(255,255,255,0.1)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
                      {statuses.filter(s => s !== 'all').map((s) => (
                        <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Users
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateKYC = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/kyc/${userId}?status=${status}`);
      toast.success("KYC status updated!");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update KYC");
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="text-2xl font-bold text-white mb-6">Users ({users.length})</h1>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-[#121212] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Phone</th>
                <th>KYC Status</th>
                <th>Affiliate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <p className="text-white">{user.name}</p>
                      <p className="text-sm text-[#737373]">{user.email}</p>
                    </div>
                  </td>
                  <td className="text-[#A3A3A3]">{user.phone}</td>
                  <td>
                    <span className={`status-badge ${user.kyc_status === 'verified' ? 'delivered' : 'pending'}`}>
                      {user.kyc_status}
                    </span>
                  </td>
                  <td>
                    {user.is_affiliate ? (
                      <span className="text-[#D4AF37] font-mono">{user.affiliate_code}</span>
                    ) : (
                      <span className="text-[#737373]">No</span>
                    )}
                  </td>
                  <td>
                    {user.kyc_status !== 'verified' && user.kyc_documents?.length > 0 && (
                      <div className="flex gap-2">
                        <button 
                          onClick={() => updateKYC(user.id, 'verified')}
                          className="p-2 hover:bg-[rgba(255,255,255,0.05)] text-[#00E676]"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => updateKYC(user.id, 'rejected')}
                          className="p-2 hover:bg-[rgba(255,255,255,0.05)] text-[#FF3B30]"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Admin Affiliates
const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [affiliatesRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/admin/affiliates`),
        axios.get(`${API}/admin/affiliate-applications`)
      ]);
      setAffiliates(affiliatesRes.data);
      setApplications(applicationsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const processApplication = async (appId, approve) => {
    try {
      await axios.put(`${API}/admin/affiliate-applications/${appId}?approve=${approve}`);
      toast.success(approve ? "Application approved!" : "Application rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to process application");
    }
  };

  return (
    <div data-testid="admin-affiliates">
      <h1 className="text-2xl font-bold text-white mb-6">Affiliates</h1>

      {/* Pending Applications */}
      {applications.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Pending Applications ({applications.length})</h2>
          <div className="space-y-4">
            {applications.map((app) => (
              <div key={app.id} className="dashboard-card">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <p className="text-white font-medium">{app.user_name || 'Unknown User'}</p>
                    <p className="text-sm text-[#A3A3A3]">{app.user_email}</p>
                    {app.user_phone && <p className="text-sm text-[#737373]">{app.user_phone}</p>}
                    <p className="text-sm text-[#737373] mt-2 italic">"{app.reason}"</p>
                    <p className="text-xs text-[#525252] mt-1">
                      Applied: {new Date(app.created_at).toLocaleDateString('en-IN', { 
                        day: 'numeric', month: 'short', year: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      onClick={() => processApplication(app.id, true)} 
                      className="btn-gold px-6 py-2"
                      data-testid={`approve-${app.id}`}
                    >
                      Approve
                    </Button>
                    <Button 
                      onClick={() => processApplication(app.id, false)} 
                      className="btn-outline px-6 py-2"
                      data-testid={`reject-${app.id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Affiliates */}
      <h2 className="text-lg font-semibold text-white mb-4">Active Affiliates ({affiliates.length})</h2>
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-[#121212] animate-pulse" />
          ))}
        </div>
      ) : affiliates.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <p className="text-[#737373]">No affiliates yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Affiliate</th>
                <th>Code</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((affiliate) => (
                <tr key={affiliate.id}>
                  <td>
                    <div>
                      <p className="text-white">{affiliate.name}</p>
                      <p className="text-sm text-[#737373]">{affiliate.email}</p>
                    </div>
                  </td>
                  <td>
                    <span className="text-[#D4AF37] font-mono">{affiliate.affiliate_code}</span>
                  </td>
                  <td className="text-[#A3A3A3]">
                    {new Date(affiliate.created_at).toLocaleDateString('en-IN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
